import type { SalesforceProjectMembersResponse } from 'backend/types'
import type { AnoInquiryStatus } from 'shared'
import { apiFetch } from '..'

/**
 * Salesforceプロジェクト型
 */
export interface SalesforceProject {
  id: number
  ano: string
  name: string
  customerName: string
  totalAmount: number
}

/**
 * Salesforceプロジェクト一覧レスポンス
 */
export interface GetSalesforceProjectsResponse {
  total: number
  projects: SalesforceProject[]
}

/**
 * チャットメンバー型
 */
export interface ChatMember {
  memberId: number
  memberEmail: string
  memberName: string
  divisionName: string
  roleName: string
  customerName: string
}

/**
 * チャットメンバー一覧レスポンス
 */
export interface GetChatMembersResponse {
  total: number
  members: ChatMember[]
}

/**
 * Salesforceプロジェクト一覧取得API
 *
 * @param params - クエリパラメータ
 * @returns プロジェクト一覧レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function getSalesforceProjects(params?: {
  all?: boolean
  salesforceProjectMemberTypeId?: number
  salesOn?: string
  ano?: string
  offset?: number
  limit?: number
}): Promise<GetSalesforceProjectsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.all) {
    queryParams.append('all', 'true')
  }
  if (params?.salesforceProjectMemberTypeId !== undefined) {
    queryParams.append('salesforceProjectMemberTypeId', params.salesforceProjectMemberTypeId.toString())
  }
  if (params?.salesOn) {
    queryParams.append('salesOn', params.salesOn)
  }
  if (params?.ano) {
    queryParams.append('ano', params.ano)
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/opportunity/salesforce/projects${queryString ? `?${queryString}` : ''}`

  return apiFetch(url, { method: 'GET' })
}

/**
 * Salesforceプロジェクトメンバー一覧取得API
 *
 * 指定したSalesforceプロジェクトに所属するメンバー一覧を取得します。
 *
 * @param salesforceProjectId - SalesforceプロジェクトID
 * @param params - クエリパラメータ
 * @param params.offset - オフセット（デフォルト: 0）
 * @param params.limit - 上限（オプション）
 * @returns Salesforceプロジェクトメンバー一覧レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、プロジェクト未発見（404）、サーバーエラー（500）
 */
export async function getSalesforceProjectMembers(
  salesforceProjectId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<SalesforceProjectMembersResponse> {
  // クエリパラメータを構築
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }
  const queryString = queryParams.toString()
  const url = `/api/salesforce/${salesforceProjectId}/members${queryString ? `?${queryString}` : ''}`

  return apiFetch<SalesforceProjectMembersResponse>(url, { method: 'GET' })
}

/**
 * 顧客型
 */
export interface SalesforceCustomer {
  id: number
  name: string
  client_id: number | null
}

/**
 * 顧客一覧レスポンス
 */
export interface GetSalesforceCustomersResponse {
  total: number
  customers: SalesforceCustomer[]
}

/**
 * 顧客一覧取得API
 *
 * 顧客は数万件規模あるため全件取得は行わず、顧客名 `q` の部分一致で絞り込む。
 * `q` が空の場合バックエンドは空配列を返す。
 * @returns 顧客一覧レスポンス
 */
export async function getSalesforceCustomers(params?: {
  q?: string
  limit?: number
}): Promise<GetSalesforceCustomersResponse> {
  const queryParams = new URLSearchParams()
  if (params?.q) {
    queryParams.append('q', params.q)
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }
  const queryString = queryParams.toString()
  const url = `/api/salesforce-customers/customers${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetSalesforceCustomersResponse>(url, { method: 'GET' })
}

/**
 * Salesforce商談型
 */
export interface SalesforceDeal {
  id: number
  salesforce_deal_id: string
  salesforce_deal_name: string
  salesforce_customer_id: number
  customer_name: string
  has_inquiry: boolean
  has_ano: boolean
}

/**
 * Salesforce商談一覧レスポンス型
 */
export interface GetSalesforceDealsResponse {
  total: number
  salesforce_deals: SalesforceDeal[]
}

/**
 * Salesforce商談一覧取得API
 *
 * @param salesforceCustomerId - Salesforce顧客ID
 * @returns 商談一覧レスポンス
 */
export async function getSalesforceDeals(salesforceCustomerId: number): Promise<GetSalesforceDealsResponse> {
  const params = new URLSearchParams({
    salesforce_customer_id: String(salesforceCustomerId),
  })
  return apiFetch<GetSalesforceDealsResponse>(`/api/salesforce-deals?${params}`, { method: 'GET' })
}

/**
 * Salesforce商談ANO型
 */
export interface SalesforceDealAno {
  salesforce_project_id: number
  ano: string
  name: string
  enabled: boolean
  inquiry_status: AnoInquiryStatus | null
  lateral_division_id: number | null
}

/**
 * Salesforce商談ANO一覧レスポンス型
 */
export interface GetSalesforceDealAnosResponse {
  total: number
  anos: SalesforceDealAno[]
}

/**
 * Salesforce商談ANO一覧取得API
 *
 * @param dealId - 商談ID
 * @returns ANO一覧レスポンス
 */
export async function getSalesforceDealAnos(dealId: number): Promise<GetSalesforceDealAnosResponse> {
  return apiFetch<GetSalesforceDealAnosResponse>(`/api/salesforce-deal/${dealId}/anos`, { method: 'GET' })
}

/**
 * チャットメンバー候補取得API
 *
 * 案件に関連するチャットメンバー候補（プロジェクトメンバー、マネージャー、フォローメンバー等）を取得します。
 *
 * @param salesforceProjectId - SalesforceプロジェクトID
 * @returns チャットメンバー一覧
 * @throws ApiError - 認証失敗（401）、権限不足（403）、案件未発見（404）、サーバーエラー（500）
 */
export async function getChatMembers(salesforceProjectId: number): Promise<GetChatMembersResponse> {
  return apiFetch(`/api/opportunity/chat-members?salesforceProjectId=${salesforceProjectId}`, {
    method: 'GET',
  })
}
